// Public homepage — football + cricket predictions list.
// During the WC window (May 26 – Jul 19 2026) it falls through to `WorldCupHome` (takeover mode).
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Trophy,
  BarChart3,
  Globe,
  Users,
  ChevronRight,
  Zap,
  Clock,
  Target,
  RefreshCw,
} from "lucide-react";
import { MidPageBanner, InlineBanner } from "../components/banners";
import { API } from "../utils/apiUrl";
import { Particles, AnimatedStat, MatchSkeleton } from "../components/common/Skeletons";
import LeaguePromoRotator from "../components/news/LeaguePromoRotator";
import HomeNewsStrip from "../components/news/HomeNewsStrip";

// WC 2026 is archived (still browsable at /worldcup) — homepage now focuses on
// league news, predictions and betting.
const HomePage = () => <RegularHomePage />;

const RegularHomePage = () => {
  const { t } = useTranslation();
  const [upcomingMatches, setUpcomingMatches] = useState({ title: "", description: "", matches: [] });
  const [leagueMatches, setLeagueMatches] = useState({ title: "", description: "", matches: [] });
  const [cricketMatches, setCricketMatches] = useState({ title: "", description: "", matches: [] });
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState("");
  const [error, setError] = useState(null);

  const fetchPredictions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/predictions`, { timeout: 15000 });
      setUpcomingMatches(response.data.upcomingMatches || { title: "", description: "", matches: [] });
      setLeagueMatches(response.data.leagueMatches || { title: "", description: "", matches: [] });
      setCricketMatches(response.data.cricketMatches || { title: "", description: "", matches: [] });
      setLastUpdated(response.data.lastUpdated || "");
    } catch (err) {
      console.error("Error fetching predictions:", err);
      if (err.code === "ERR_NETWORK" || err.code === "ECONNREFUSED") {
        setError("Unable to connect to server. Please try again later.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPredictions();
  }, []);

  const formatLastUpdated = (isoString) => {
    if (!isoString) return "";
    try {
      const date = new Date(isoString);
      return date.toLocaleString("en-GB", {
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        timeZoneName: "short",
      });
    } catch {
      return "";
    }
  };

  const MatchCard = ({ match, index, showInlineBanner = false }) => (
    <>
      <div
        key={match.id}
        className="match-card card-shine"
        style={{ animationDelay: `${index * 0.05}s` }}
        data-testid={`match-card-${match.id}`}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-3">
              <span className="badge-sport">{match.league}</span>
              <span className="text-xs text-slate-500 uppercase">
                {match.sport === "cricket" ? "🏏" : "⚽"}
              </span>
            </div>
            <h3 className="text-xl font-bold text-white mb-2">
              {match.teamA} vs {match.teamB}
            </h3>
            <p className="text-slate-400 text-sm mb-3">{match.shortSummary}</p>
            <div className="flex items-center gap-4 text-sm flex-wrap">
              <span className="text-slate-500">
                <Clock className="w-4 h-4 inline mr-1" />
                {match.date}, {match.kickoff}
              </span>
              <span className="text-green-400 font-semibold">
                <Target className="w-4 h-4 inline mr-1" />
                {match.prediction} ({match.confidence})
              </span>
            </div>
          </div>
          <div className="flex-shrink-0">
            <Link
              to={`/predictions/${match.id}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-green-500 hover:bg-green-400 text-slate-950 font-bold rounded-full transition-all hover:scale-105"
              data-testid={`view-prediction-${match.id}`}
              data-track="view-prediction"
              data-track-text={`${match.teamA} vs ${match.teamB}`}
            >
              {t("matches.viewPrediction")}
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
      {showInlineBanner && (index + 1) % 5 === 0 && <InlineBanner className="my-4" />}
    </>
  );

  return (
    <>
      {/* Hero */}
      <section className="hero-bg min-h-[70vh] flex items-center justify-center pt-24 pb-16 px-4 relative">
        <Particles />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <span className="inline-block px-4 py-2 bg-green-500/20 text-green-400 text-sm font-semibold rounded-full mb-6 border border-green-500/30 animate-pulse">
            🔥 Live Predictions Updated Daily
          </span>
          <h1
            className="text-5xl sm:text-6xl lg:text-7xl font-extrabold uppercase tracking-tight mb-4"
            style={{
              fontFamily: "'Barlow Condensed', sans-serif",
              background: "linear-gradient(135deg, #ffffff 0%, #22c55e 50%, #ffffff 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            {t("hero.title")}
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-2xl mx-auto mb-8">{t("hero.subtitle")}</p>

          <div className="grid grid-cols-3 gap-6 max-w-md mx-auto mb-8 py-6 px-4 bg-slate-800/30 rounded-2xl border border-slate-700/50 backdrop-blur-sm">
            <AnimatedStat value={t("hero.accuracyVal")} label={t("hero.accuracy")} />
            <AnimatedStat value={t("hero.usersVal")} label={t("hero.users")} />
            <AnimatedStat value={t("hero.updatesVal")} label={t("hero.updates")} />
          </div>

          <a href="#predictions" className="btn-primary text-lg" data-testid="view-picks-btn">
            <Trophy className="w-5 h-5" />
            View Today's Picks
          </a>
        </div>
      </section>

      {/* League promos → FunBet (tracked) + latest AI news */}
      <section className="max-w-6xl mx-auto px-4 pt-8 relative">
        <LeaguePromoRotator />
      </section>
      <HomeNewsStrip />

      {/* Section 1: Upcoming Matches */}
      <section id="predictions" className="py-16 px-4 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <div className="icon-glow w-10 h-10 flex items-center justify-center bg-green-500/20 rounded-xl">
                <Zap className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  NEXT 10 MATCHES
                </h2>
                <p className="text-slate-500 text-xs">Upcoming matches from current time</p>
              </div>
            </div>
            <button
              onClick={fetchPredictions}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-full text-sm transition-all disabled:opacity-50"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
              {t("analytics.refresh")}
            </button>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 text-red-400 text-center" data-testid="error-message">
              {error}
            </div>
          )}

          <div className="space-y-4" data-testid="upcoming-matches-section">
            {loading ? (
              <>
                <MatchSkeleton />
                <MatchSkeleton />
                <MatchSkeleton />
              </>
            ) : upcomingMatches.matches.length === 0 ? (
              <div className="text-center py-12 text-slate-400 bg-slate-800/30 rounded-xl border border-slate-700/50">
                <p className="text-lg">No upcoming football matches at the moment.</p>
                <p className="text-sm mt-2 text-slate-500">Football data updates daily at midnight UK time.</p>
                <Link
                  to="/ipl"
                  className="inline-flex items-center gap-2 mt-4 px-6 py-2 bg-orange-500 hover:bg-orange-400 text-white font-bold rounded-full transition-all"
                >
                  🏏 View IPL 2026 Predictions
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              upcomingMatches.matches.map((match, index) => (
                <MatchCard key={match.id} match={match} index={index} showInlineBanner={true} />
              ))
            )}
          </div>
        </div>
      </section>

      <MidPageBanner className="max-w-4xl mx-auto px-4" />

      {/* Section 2: Top European Leagues */}
      <section className="py-16 px-4 bg-slate-950">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-3 mb-8">
            <div className="icon-glow w-10 h-10 flex items-center justify-center bg-blue-500/20 rounded-xl">
              <Globe className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                TOP EUROPEAN LEAGUES
              </h2>
              <p className="text-slate-500 text-xs">
                5 Premier League • 5 La Liga • 5 Champions League • 5 Other European
              </p>
            </div>
          </div>

          {lastUpdated && (
            <p className="text-slate-500 text-xs mb-6">Last updated: {formatLastUpdated(lastUpdated)}</p>
          )}

          <div className="space-y-4" data-testid="league-matches-section">
            {loading ? (
              <>
                <MatchSkeleton />
                <MatchSkeleton />
                <MatchSkeleton />
              </>
            ) : leagueMatches.matches.length === 0 ? (
              <div className="text-center py-12 text-slate-400 bg-slate-800/30 rounded-xl border border-slate-700/50">
                <p className="text-lg">No European league matches at the moment.</p>
                <p className="text-sm mt-2 text-slate-500">League data updates daily at midnight UK time.</p>
                <Link
                  to="/ipl"
                  className="inline-flex items-center gap-2 mt-4 px-6 py-2 bg-orange-500 hover:bg-orange-400 text-white font-bold rounded-full transition-all"
                >
                  🏏 View IPL 2026 Predictions
                  <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            ) : (
              leagueMatches.matches.map((match, index) => (
                <MatchCard key={match.id} match={match} index={index} showInlineBanner={true} />
              ))
            )}
          </div>
        </div>
      </section>

      {/* Section 3: Cricket */}
      {cricketMatches.matches.length > 0 && (
        <section className="py-16 px-4 bg-gradient-to-b from-slate-950 to-slate-900">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-3 mb-8">
              <div className="icon-glow w-10 h-10 flex items-center justify-center bg-amber-500/20 rounded-xl">
                <span className="text-xl">🏏</span>
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
                  LIVE CRICKET
                </h2>
                <p className="text-slate-500 text-xs">International & domestic cricket matches</p>
              </div>
            </div>

            <div className="space-y-4" data-testid="cricket-matches-section">
              {loading ? (
                <>
                  <MatchSkeleton />
                  <MatchSkeleton />
                </>
              ) : (
                cricketMatches.matches.map((match, index) => (
                  <MatchCard key={match.id} match={match} index={index} showInlineBanner={false} />
                ))
              )}
            </div>
          </div>
        </section>
      )}

      {/* Features */}
      <section className="py-20 px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900/50 to-slate-950"></div>
        <div className="max-w-5xl mx-auto relative z-10">
          <h2 className="section-title text-center mb-4">WHY TRUST SPORTSGURU247</h2>
          <p className="text-slate-400 text-center mb-12 max-w-xl mx-auto">
            Powered by real-time data, trusted by thousands of sports fans worldwide
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="feature-card card-shine">
              <div className="icon-glow w-12 h-12 flex items-center justify-center bg-green-500/20 rounded-xl shrink-0">
                <BarChart3 className="w-6 h-6 text-green-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Live Data</h3>
                <p className="text-slate-400 text-sm">Real-time match data from official APIs, updated daily at midnight UK time.</p>
              </div>
            </div>
            <div className="feature-card card-shine">
              <div className="icon-glow w-12 h-12 flex items-center justify-center bg-blue-500/20 rounded-xl shrink-0">
                <Zap className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Expert Analysis</h3>
                <p className="text-slate-400 text-sm">Detailed match previews with form analysis and head-to-head statistics.</p>
              </div>
            </div>
            <div className="feature-card card-shine">
              <div className="icon-glow w-12 h-12 flex items-center justify-center bg-purple-500/20 rounded-xl shrink-0">
                <Globe className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Global Coverage</h3>
                <p className="text-slate-400 text-sm">Football from Premier League, La Liga, Serie A, Bundesliga + IPL Cricket.</p>
              </div>
            </div>
            <div className="feature-card card-shine">
              <div className="icon-glow w-12 h-12 flex items-center justify-center bg-amber-500/20 rounded-xl shrink-0">
                <Users className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-white mb-2">Trusted Analysis</h3>
                <p className="text-slate-400 text-sm">Join thousands of sports fans who rely on our insights for match predictions.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default HomePage;
